import { useEffect, useMemo, useState } from "react";
import {
  bannerTextForLanguage,
  DEFAULT_SITE_BANNER,
  normalizeBannerColor,
  type SiteBanner,
} from "@/data/banner";
import { BANNER_API_URL } from "@/config/constants";
import { useAppTranslation } from "@/i18n/useAppTranslation";

const BANNER_HEIGHT = "2rem";

function piecesForTrack(text: string): string[] {
  const pieces: string[] = [];
  let total = 0;
  const goal = Math.max(96, text.length * 2);
  while (total < goal && pieces.length < 16) {
    pieces.push(text);
    total += text.length + 4;
  }
  return pieces.length > 0 ? pieces : [text];
}

function TickerCopy({ pieces }: { pieces: string[] }) {
  return (
    <div className="flex shrink-0 items-center">
      {pieces.map((piece, index) => (
        <span
          key={`${piece}-${index}`}
          className="inline-flex items-center whitespace-nowrap px-6 text-xs font-bold uppercase tracking-[0.28em]"
        >
          {piece}
        </span>
      ))}
    </div>
  );
}

function TickerTrack({ text, durationSec }: { text: string; durationSec: number }) {
  const pieces = useMemo(() => piecesForTrack(text), [text]);
  return (
    <>
      <div
        className="ticker-track flex w-max"
        style={{ ["--ticker-duration" as string]: `${durationSec}s` }}
        aria-hidden="true"
      >
        <TickerCopy pieces={pieces} />
        <TickerCopy pieces={pieces} />
      </div>
      <p aria-hidden="true" className="ticker-static px-4 text-center text-xs font-bold uppercase tracking-[0.28em] truncate">
        {text}
      </p>
    </>
  );
}

export function AnnouncementTicker({
  previewText,
  previewColor,
}: {
  previewText?: string;
  previewColor?: string;
}) {
  const isPreview = previewText !== undefined;
  const { i18n } = useAppTranslation();
  const [banner, setBanner] = useState<SiteBanner | null>(
    isPreview
      ? {
          enabled: true,
          textEs: previewText,
          textEn: previewText,
          backgroundColor: normalizeBannerColor(previewColor),
        }
      : null,
  );

  useEffect(() => {
    if (isPreview) {
      setBanner({
        enabled: Boolean(previewText.trim()),
        textEs: previewText,
        textEn: previewText,
        backgroundColor: normalizeBannerColor(previewColor),
      });
      return;
    }

    const controller = new AbortController();
    fetch(BANNER_API_URL, { signal: controller.signal, cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error("banner unavailable");
        return (await res.json()) as Partial<SiteBanner>;
      })
      .then((next) => {
        setBanner({
          enabled: Boolean(next.enabled),
          textEs: next.textEs ?? "",
          textEn: next.textEn ?? "",
          backgroundColor: normalizeBannerColor(next.backgroundColor),
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setBanner(DEFAULT_SITE_BANNER);
      });

    return () => controller.abort();
  }, [isPreview, previewText, previewColor]);

  const text = banner ? bannerTextForLanguage(banner, i18n.language) : "";
  const visible = Boolean(banner?.enabled && text);

  useEffect(() => {
    if (isPreview) return;
    document.documentElement.style.setProperty("--banner-h", visible ? BANNER_HEIGHT : "0px");
    return () => {
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [isPreview, visible]);

  if (!visible || !banner) return null;

  const durationSec = Math.max(18, Math.min(55, Math.round(text.length * 0.32) + 14));
  const color = normalizeBannerColor(banner.backgroundColor);

  return (
    <div
      role="region"
      aria-label={text}
      className={
        isPreview
          ? "relative flex h-8 items-center overflow-hidden text-white"
          : "fixed top-0 inset-x-0 z-[60] flex h-8 items-center overflow-hidden text-white"
      }
      style={{ backgroundColor: color }}
    >
      <TickerTrack text={text} durationSec={durationSec} />
    </div>
  );
}
